import type { GamePhase, Player } from "@cloudnest/horror-race-engine";
import { Transition } from "@headlessui/react";
import type { ComponentProps } from "react";
import { ConnectionBadge } from "./ConnectionBadge";
import { RoundInfo } from "./RoundInfo";
import { TopBanner, type TopBannerMode } from "./TopBanner";
import { Button } from "./ui/button";

type ConnectionStatus = ComponentProps<typeof ConnectionBadge>["status"];

interface HorrorTopFrameProps {
  // ── title bar ─────────────────────────────────────────────────────────
  readonly connectionStatus: ConnectionStatus;
  readonly onLeave: () => void;
  /** Defaults to "나가기"; pass "관전 나가기" for observers. */
  readonly leaveLabel?: string;

  // ── RoundInfo ─────────────────────────────────────────────────────────
  readonly phase: GamePhase;
  readonly raceNumber: number;
  readonly totalRaces: number;
  readonly round: number;
  readonly poolSize: number;
  /** Render the 라운드 slot as a ghost (label-only). See RoundInfo. */
  readonly roundGhost?: boolean;
  /** Render the 남은 코인 slot as a ghost. See RoundInfo. */
  readonly poolGhost?: boolean;
  /** M1: promote the 남은 코인 slot to a button that opens the coin-memory
   *  sheet. See RoundInfo. */
  readonly onOpenMemory?: () => void;
  /** M1: gates the memory button. False → disabled. See RoundInfo. */
  readonly memoryEnabled?: boolean;

  // ── TopBanner (player rail) ───────────────────────────────────────────
  readonly players: readonly Player[];
  readonly turnOrder: readonly number[];
  readonly currentTurnIndex: number;
  readonly drawPass: number;
  readonly forfeitPlayerIndex: number | null;
  readonly fewPlayer: boolean;
  readonly garnets: Readonly<Record<string, number>>;
  readonly myPlayerId: string | null;
  readonly showSecretFog?: boolean;
  /** Rail mode: "turn" (default, caret/pass) or "ready" (confirm/선택 완료). */
  readonly railMode?: TopBannerMode;
  /** Per-player ready flags by `player.id` — required when `railMode === "ready"`. */
  readonly readyFlags?: Readonly<Record<string, boolean>>;

  // ── error overlay ─────────────────────────────────────────────────────
  /** When non-null, an absolute-positioned overlay appears below the RoundInfo
   * (overlaying the rail area). Layout is NOT pushed — rail Y stays fixed. */
  readonly errorMessage: string | null;
  readonly onDismissError: () => void;
}

/**
 * D3 — shared top frame for every play-loop / recap screen: title bar +
 * RoundInfo (4-slot, ghost-aware) + TopBanner (player rail). Error renders as
 * an absolute overlay so the rail Y is invariant across phases.
 *
 * PR-1 scope: extract from inline GameScreen markup. Used by SETUP +
 * ROUND_DRAW_AND_PLACE + ROUND_RESOLVE. Recap views still self-contained
 * (migrate in PR-3 / PR-4). Rail stays in TURN mode (READY mode lands in PR-2).
 *
 * Returns a Fragment of two flex children (top-bar div + TopBanner) so it can
 * be dropped directly into a `flex flex-col overflow-hidden` parent without
 * altering the existing layout.
 */
export function HorrorTopFrame({
  connectionStatus,
  onLeave,
  leaveLabel = "나가기",
  phase,
  raceNumber,
  totalRaces,
  round,
  poolSize,
  roundGhost,
  poolGhost,
  onOpenMemory,
  memoryEnabled,
  players,
  turnOrder,
  currentTurnIndex,
  drawPass,
  forfeitPlayerIndex,
  fewPlayer,
  garnets,
  myPlayerId,
  showSecretFog,
  railMode,
  readyFlags,
  errorMessage,
  onDismissError,
}: HorrorTopFrameProps) {
  return (
    <>
      <div
        data-testid="horror-top-bar"
        className="relative flex shrink-0 flex-col gap-2 px-4 py-2.5"
        style={{
          borderBottom: "1px solid var(--cn-horror-surface-border)",
          backgroundColor: "var(--cn-horror-surface-secondary)",
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <h1
            className="font-bold text-base"
            style={{ color: "var(--cn-horror-p1)" }}
          >
            호러 레이스
          </h1>
          <div className="flex items-center gap-3">
            <ConnectionBadge status={connectionStatus} />
            <Button
              data-testid="leave-game-btn"
              onClick={onLeave}
              variant="ghost"
              size="sm"
            >
              {leaveLabel}
            </Button>
          </div>
        </div>

        <RoundInfo
          phase={phase}
          raceNumber={raceNumber}
          totalRaces={totalRaces}
          round={round}
          poolSize={poolSize}
          roundGhost={roundGhost}
          poolGhost={poolGhost}
          onOpenMemory={onOpenMemory}
          memoryEnabled={memoryEnabled}
        />

        {/* Error overlay — absolute so it doesn't push the rail down. Sits just
            below the top-bar (over the TopBanner area) when an error is active. */}
        <Transition
          show={!!errorMessage}
          enter="transition-all duration-200 ease-out"
          enterFrom="opacity-0 -translate-y-2"
          enterTo="opacity-100 translate-y-0"
          leave="transition-all duration-150 ease-in"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-2"
        >
          <div
            data-testid="horror-error-overlay"
            className="absolute right-4 left-4 z-10 flex items-center justify-between rounded-md px-3 py-2 text-xs"
            style={{
              top: "calc(100% - 4px)",
              backgroundColor: "var(--cn-horror-error-bg)",
              color: "var(--cn-horror-error)",
              border: "1px solid var(--cn-horror-error)",
              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.5)",
            }}
          >
            <span>{errorMessage}</span>
            <button
              type="button"
              onClick={onDismissError}
              className="shrink-0 cursor-pointer border-none bg-transparent text-[11px] underline"
              style={{ color: "var(--cn-horror-error)" }}
            >
              닫기
            </button>
          </div>
        </Transition>
      </div>

      <TopBanner
        players={players}
        turnOrder={turnOrder}
        currentTurnIndex={currentTurnIndex}
        drawPass={drawPass}
        forfeitPlayerIndex={forfeitPlayerIndex}
        fewPlayer={fewPlayer}
        round={round}
        raceNumber={raceNumber}
        totalRaces={totalRaces}
        garnets={garnets}
        myPlayerId={myPlayerId}
        showSecretFog={showSecretFog}
        mode={railMode}
        readyFlags={readyFlags}
      />
    </>
  );
}
